import { Injectable } from '@angular/core';
import { RouterState } from '@angular/router';
import { LocationAcl, LocationAclGroups, LocationAclMembers, SecurityService } from '@era/locations-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { LocationsRouterStoreSelectors } from '../router';
import {
  LocationPermissionsResolverActions,
  AccessControlListApiActions,
  LocationAclPermissionsUiActions,
  LocationPermissionsComponentActions,
} from './location-acl.actions';
import { safeMap } from '../effects.helpers';

@Injectable()
export class LocationAclEffects {
  constructor(
    private readonly securityService: SecurityService,
    private readonly routeStore: Store<RouterState>,
    private readonly actions$: Actions
  ) {}

  locationAclLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationPermissionsResolverActions.locationAclRequested),
      mergeMap(({ resourceId }) =>
        safeMap(
          this.securityService.getLocationAcls(resourceId),
          (locationAcl: LocationAcl) => AccessControlListApiActions.locationAclLoaded({ locationAcl }),
          (error) => AccessControlListApiActions.locationAclLoadedError({ error })
        )
      )
    );
  });

  locationAclMemberPutEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationAclPermissionsUiActions.locationAclMemberPut),
      mergeMap(({ aclMember }) =>
        this.securityService
          .putLocationAclMembers(aclMember.resourceId, aclMember)
          .pipe(
            map((locationAclMembers: LocationAclMembers) =>
              AccessControlListApiActions.locationAclMemberPut({ aclMember: locationAclMembers })
            )
          )
      )
    );
  });

  locationAclGroupPutEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationAclPermissionsUiActions.locationAclGroupPut),
      mergeMap(({ aclGroup }) =>
        this.securityService
          .putLocationAclGroups(aclGroup.resourceId, aclGroup)
          .pipe(
            map((locationAclGroups: LocationAclGroups) =>
              AccessControlListApiActions.locationAclGroupPut({ aclGroup: locationAclGroups })
            )
          )
      )
    );
  });

  locationComponentAddGroupEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationPermissionsComponentActions.addGroup),
      withLatestFrom(this.routeStore.select(LocationsRouterStoreSelectors.selectLocationId)),
      mergeMap(([{ group }, location]) =>
        this.securityService
          .putLocationAclGroups(location, {
            resourceId: location,
            groupId: group.id,
            allow: new Set(),
            deny: new Set(),
          })
          .pipe(
            map((locationAclGroups: LocationAclGroups) =>
              AccessControlListApiActions.locationAclGroupPut({ aclGroup: locationAclGroups })
            )
          )
      )
    );
  });

  locationAclGroupDeleteEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationAclPermissionsUiActions.locationAclGroupRemove),
      mergeMap(({ aclGroup }) =>
        this.securityService
          .deleteLocationAclGroups(aclGroup.resourceId, aclGroup)
          .pipe(map(() => AccessControlListApiActions.locationAclGroupRemove({ aclGroup })))
      )
    );
  });
}
